<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import BulkSendsAPI from 'dashboard/api/bulkSends';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const props = defineProps({
  // Contacts ticked on the Contacts page. Empty when opened from Campaigns,
  // where the audience is picked by label instead.
  contactIds: { type: Array, default: () => [] },
});

const emit = defineEmits(['sent']);

const { t } = useI18n();
const store = useStore();

// Values typed into a variable may use these; the server renders them per
// contact, so one mapping produces a personalised message for everybody.
const CONTACT_TOKENS = [
  '{{contact.first_name}}',
  '{{contact.name}}',
  '{{contact.phone_number}}',
  '{{contact.email}}',
  '{{contact.custom_attribute.KEY}}',
];

const inboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const labels = useMapGetter('labels/getLabels');
const getTemplates = useMapGetter('inboxes/getFilteredWhatsAppTemplates');

const dialogRef = ref(null);
const parserRef = ref(null);
const inboxId = ref(null);
const templateId = ref(null);
const labelIds = ref([]);
const isSending = ref(false);
// Empty means send now. A local datetime-local value, converted to UTC on send.
const sendAt = ref('');

const minSendAt = computed(() => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
});

const inboxOptions = computed(() =>
  inboxes.value.map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const templates = computed(() =>
  inboxId.value ? getTemplates.value(inboxId.value) : []
);

const templateOptions = computed(() =>
  templates.value.map(template => ({
    value: template.id,
    label: `${template.name} (${template.language || 'en'})`,
  }))
);

const selectedTemplate = computed(() =>
  templates.value.find(template => template.id === templateId.value)
);

const labelOptions = computed(() =>
  labels.value.map(label => ({ value: label.id, label: label.title }))
);

const hasAudience = computed(
  () => props.contactIds.length > 0 || labelIds.value.length > 0
);

const canSend = computed(
  () =>
    !!selectedTemplate.value &&
    hasAudience.value &&
    parserRef.value?.isFormInvalid === false
);

watch(inboxId, () => {
  templateId.value = null;
});

const copyToken = async token => {
  await copyTextToClipboard(token);
  useAlert(t('BULK_SEND.DIALOG.TOKEN_COPIED', { token }));
};

// The parser validates the variables and emits the same payload the "new
// conversation" dialog sends, in raw_template mode so each recipient's copy is
// rendered against their own contact.
const send = async ({ message, templateParams }) => {
  isSending.value = true;
  try {
    const { data } = await BulkSendsAPI.create({
      inbox_id: inboxId.value,
      content: message,
      template_params: templateParams,
      contact_ids: props.contactIds,
      label_ids: labelIds.value,
      send_at: sendAt.value ? new Date(sendAt.value).toISOString() : null,
    });
    const queued = data.recipients.filter(r => r.status === 'pending').length;
    const skipped = data.recipients.length - queued;
    useAlert(t('BULK_SEND.DIALOG.QUEUED', { queued, skipped }));
    dialogRef.value.close();
    emit('sent', data);
  } catch (error) {
    useAlert(error?.response?.data?.error || t('BULK_SEND.DIALOG.FAILED'));
  } finally {
    isSending.value = false;
  }
};

const open = () => {
  inboxId.value = null;
  templateId.value = null;
  labelIds.value = [];
  sendAt.value = '';
  // Refresh so templates synced since the page loaded are on offer.
  store.dispatch('inboxes/get');
  dialogRef.value.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="t('BULK_SEND.DIALOG.TITLE')"
    :description="
      contactIds.length
        ? t('BULK_SEND.DIALOG.DESCRIPTION_SELECTED', {
            count: contactIds.length,
          })
        : t('BULK_SEND.DIALOG.DESCRIPTION_LABELS')
    "
    :confirm-button-label="t('BULK_SEND.DIALOG.SEND')"
    :disable-confirm-button="!canSend || isSending"
    :is-loading="isSending"
    @confirm="parserRef?.sendMessage()"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('BULK_SEND.DIALOG.INBOX') }}
        </label>
        <ComboBox
          v-model="inboxId"
          :options="inboxOptions"
          :placeholder="t('BULK_SEND.DIALOG.INBOX_PLACEHOLDER')"
        />
      </div>

      <div v-if="inboxId" class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('BULK_SEND.DIALOG.TEMPLATE') }}
        </label>
        <ComboBox
          v-model="templateId"
          :options="templateOptions"
          :placeholder="t('BULK_SEND.DIALOG.TEMPLATE_PLACEHOLDER')"
        />
      </div>

      <template v-if="selectedTemplate">
        <WhatsAppTemplateParser
          ref="parserRef"
          :template="selectedTemplate"
          @send-message="send"
        />
        <div class="flex flex-col gap-2">
          <p class="text-xs text-n-slate-11">
            {{ t('BULK_SEND.DIALOG.TOKENS_HINT') }}
          </p>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="token in CONTACT_TOKENS"
              :key="token"
              type="button"
              class="px-2 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3 font-mono"
              @click="copyToken(token)"
            >
              {{ token }}
            </button>
          </div>
        </div>
      </template>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('BULK_SEND.DIALOG.LABELS') }}
        </label>
        <TagMultiSelectComboBox
          v-model="labelIds"
          :options="labelOptions"
          :label="t('BULK_SEND.DIALOG.LABELS')"
          :placeholder="t('BULK_SEND.DIALOG.LABELS_PLACEHOLDER')"
        />
      </div>

      <Input
        v-model="sendAt"
        type="datetime-local"
        :min="minSendAt"
        :label="t('BULK_SEND.DIALOG.SEND_AT')"
        :message="t('BULK_SEND.DIALOG.SEND_AT_HINT')"
      />

      <p class="text-xs text-n-slate-11">
        {{ t('BULK_SEND.DIALOG.PACING_NOTE') }}
      </p>
    </div>
  </Dialog>
</template>
